import { Scale, Landmark, Building2, Database, BrainCircuit, UserCheck, Lock, ExternalLink } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import { Reveal, SectionHeading } from "../components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1569169507605-f91e088ce91a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0b3dlcnMlMjBuaWdodCUyMGNpdHklMjBsaWdodHMlMjBkYXJrfGVufDB8fHx8MTc5MDYxNDE2N3ww&ixlib=rb-4.1.0&q=85";
const atlasIcons = [BrainCircuit, Landmark, UserCheck];
const techIcons = [Database, BrainCircuit, UserCheck, Lock];

export default function Eeco() {
  const { t } = useLang();
  const e = t.eeco;

  return (
    <>
      <PageHero
        testid="eeco-hero"
        eyebrow={e.eyebrow}
        titleLines={e.title}
        paragraphs={[e.p1, e.p2]}
        image={HERO_IMG}
      />

      {/* ATLAS platform */}
      <section className="py-20 lg:py-28" data-testid="eeco-atlas">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={e.atlasEyebrow} title={e.atlasTitle} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {e.atlas.map((m, i) => {
              const Icon = atlasIcons[i];
              return (
                <Reveal key={m.code} delay={(i % 3) * 0.08} className="h-full">
                  <div
                    className="group relative h-full rounded-lg p-7 bg-obsidian/80 border border-eeco/15 hover:border-eeco/50 transition-all duration-500 hover:shadow-[0_16px_50px_rgba(16,185,129,0.08)] overflow-hidden"
                    data-testid={`eeco-atlas-card-${i}`}
                  >
                    <div className="scanlines absolute inset-0 pointer-events-none" aria-hidden="true" />
                    <div className="relative">
                      <div className="flex items-start justify-between">
                        <Icon className="w-6 h-6 text-eeco" strokeWidth={1.5} aria-hidden="true" />
                        <span className="font-mono text-[10px] tracking-[0.25em] text-slate-600">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <p className="mt-6 font-mono text-xs font-bold tracking-[0.3em] uppercase text-eeco">{m.code}</p>
                      <h3 className="mt-2.5 font-display text-lg font-bold text-slate-100">{m.name}</h3>
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 lg:py-28 border-t border-sovereign/10" data-testid="eeco-solutions">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={e.solutionsEyebrow} title={e.solutionsTitle} />
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {e.solutions.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.1} className="h-full">
                <div
                  className="glass rounded-lg p-8 lg:p-10 h-full hover:border-eeco/40 transition-colors duration-500"
                  data-testid={`eeco-solution-${i}`}
                >
                  <Scale className="w-7 h-7 text-eeco" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="mt-5 font-display text-xl font-bold text-slate-100">{s.name}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  <ul className="mt-6 space-y-2.5">
                    {s.items.map((it, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-slate-300">
                        <span className="w-1.5 h-1.5 bg-eeco rotate-45 shrink-0" aria-hidden="true" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="py-20 lg:py-28 border-t border-sovereign/10" data-testid="eeco-tech">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={e.techEyebrow} title={e.techTitle} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {e.tech.map((item, i) => {
              const Icon = techIcons[i];
              return (
                <Reveal key={item.name} delay={(i % 4) * 0.07} className="h-full">
                  <div
                    className="border border-sovereign/12 bg-obsidian/60 p-6 h-full hover:border-eeco/45 transition-colors duration-400"
                    data-testid={`eeco-tech-${i}`}
                  >
                    <Icon className="w-6 h-6 text-eeco" strokeWidth={1.5} aria-hidden="true" />
                    <h3 className="mt-4 font-display text-base font-bold text-slate-100">{item.name}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-24" data-testid="eeco-sectors">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={e.sectorsEyebrow} title={e.sectorsTitle} />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            {e.sectors.map((s, i) => (
              <Reveal key={s} delay={i * 0.06}>
                <div
                  className="flex items-center gap-3 border border-sovereign/12 bg-obsidian/60 px-5 py-5 hover:border-eeco/45 transition-colors duration-300"
                  data-testid={`eeco-sector-${i}`}
                >
                  {i === 0 ? (
                    <Scale className="w-5 h-5 text-eeco shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  ) : i === 1 ? (
                    <Landmark className="w-5 h-5 text-eeco shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  ) : (
                    <Building2 className="w-5 h-5 text-eeco shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  )}
                  <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-slate-200">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-10">
            <a
              href={e.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="eeco-external-link"
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-eeco transition-colors duration-300"
            >
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
              {e.external}
            </a>
          </Reveal>
        </div>
      </section>

      <CtaBand title={e.ctaTitle} text={e.p1} btn={e.ctaBtn} />
    </>
  );
}
